/* eslint-disable max-statements */

import { Button, Flex, Group, LoadingOverlay, Modal, Stack, Tabs, Text } from '@mantine/core';
import { zodResolver } from '@mantine/form';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { z } from 'zod';

import { Transit, TransitStatus } from '@e-origin/shared';

import { useSendOneTransit, useTransit, useUpdateTransit } from '@hooks/transit';
import { useFormWithNameInputProp } from '@hooks/useFormWithNameInputProp';
import { ReportTab } from './ReportTab';
import { SummaryTab } from './SummaryTab';

interface FinalizeTransitProps {
  transitId: string;
  onHide(): void;
}

const unloadingRemarkSchema = z.object({
  unloadingCompletion: z.string().optional(),
  unloadingDate: z.string().optional(),
  stateOfSeals: z.string().optional(),
  unloadingRemark: z.string().optional(),
  conform: z.string().optional(),
});

const transitFormSchema = z.object({
  UnloadingRemark: unloadingRemarkSchema,
  trace: z.array(z.any()),
});

type TransitFormValues = z.infer<typeof transitFormSchema>;

export const FinalizeTransit: React.FC<FinalizeTransitProps> = ({ onHide, transitId }) => {
  const [editDisabled, setEditDisabled] = useState<boolean>(null);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [stateOfSealsRequired, setStateOfSealsRequired] = useState<boolean>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { data: transit, isLoading } = useTransit(transitId);
  const updateTransitMutation = useUpdateTransit();
  const sendOneMutation = useSendOneTransit();

  const computeUnloadingRemark = (t: Transit) => ({
    unloadingCompletion: t.UnloadingRemark?.unloadingCompletion ?? '1',
    unloadingDate: `${moment(t.UnloadingRemark?.unloadingDate || new Date()).format('YYYY-MM-DD')}T00:00:00`,
    stateOfSeals: t.UnloadingRemark?.stateOfSeals,
    unloadingRemark: t.UnloadingRemark?.unloadingRemark,
    conform: Number(t.UnloadingRemark?.stateOfSeals !== '0' && !t.trace?.length).toString(),
  });

  const form = useFormWithNameInputProp<TransitFormValues>({
    initialValues: {
      UnloadingRemark: {},
      trace: [],
    },
    validate: zodResolver(transitFormSchema),
    validateInputOnBlur: true,
  });

  const confirmationModalLabels = useMemo(() => {
    if (form.values.UnloadingRemark.stateOfSeals === '0') {
      if (!transit.trace?.length) {
        return {
          title: 'State of Seals NOT OK',
          description: <Text>Are you sure you want to send “Not OK” as your State of Seals response?</Text>,
        };
      } else {
        return {
          title: 'Not conform and State of Seals NOT OK',
          description: (
            <Stack gap="xl">
              <Text>
                Are you sure you want to send “No” as your CONFORM response? This will send non-compliance remarks to customs. See details in the report
                regarding all remarks.
              </Text>

              <Text>Also check your State of Seals because it's marked as “Not OK”.</Text>
            </Stack>
          ),
        };
      }
    } else {
      return {
        title: 'Not Conform',
        description: (
          <Text>
            Are you sure you want to send “No” as your CONFORM response? This will send non-compliance remarks to customs. See details in the report regarding
            all remarks.
          </Text>
        ),
      };
    }
  }, [form.values.UnloadingRemark.stateOfSeals, transit?.trace?.length]);

  const validateStateOfSeals = (values: TransitFormValues): boolean => {
    if (stateOfSealsRequired && !values.UnloadingRemark.stateOfSeals) {
      toast.error('State of seals is required because there are seals included in this transit!');
      return false;
    }
    return true;
  };

  const onSubmit = async (values: TransitFormValues) => {
    if (!validateStateOfSeals(values)) {
      return;
    }

    if (values.UnloadingRemark.conform === '0') {
      setShowConfirmationModal(true);
    } else {
      await finalizeTransit(transit._id, values);
    }
  };

  const finalizeTransit = async (_id, values) => {
    try {
      setIsSending(true);
      await updateTransitMutation.mutateAsync({
        transit: { _id, ...values },
        overwriteTrace: true,
      });

      await sendOneMutation.mutateAsync({ id: _id });
    } catch (err) {
      // pass
    } finally {
      onHide();
    }
  };

  useEffect(() => {
    if (!transit) {
      return;
    }

    const UnloadingRemark = computeUnloadingRemark(transit);
    form.setValues({
      UnloadingRemark,
      trace: transit.trace,
    });

    setEditDisabled(transit.generalInfo.status !== TransitStatus.UNLOAD_PERMISSION);
    setStateOfSealsRequired(transit.customsState.unloadPermission.stateOfSealsRequired);
  }, [transit]);

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <LoadingOverlay visible={isLoading} h="400px" />
      <Tabs defaultValue="summary" mx={15} mb={15}>
        <Tabs.List>
          <Tabs.Tab value="summary">Summary</Tabs.Tab>
          <Tabs.Tab value="report">Report</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="summary" py={30} px={50}>
          <SummaryTab form={form} editDisabled={editDisabled} stateOfSealsRequired={stateOfSealsRequired} />
        </Tabs.Panel>

        <Tabs.Panel value="report" py={30} px={50}>
          <ReportTab form={form} />
        </Tabs.Panel>
      </Tabs>

      <Group justify="center" p="md">
        <Button type="button" onClick={onHide} size="lg" variant="transparent">
          Cancel
        </Button>

        <Button data-pw="send-btn" type="submit" loading={isSending} disabled={editDisabled} variant="filled" size="lg">
          Send
        </Button>
      </Group>

      {showConfirmationModal && (
        <Modal title={confirmationModalLabels.title} opened={showConfirmationModal} onClose={() => setShowConfirmationModal(false)} centered zIndex={9999}>
          {confirmationModalLabels.description}
          <Flex align="center" justify="center" mt="md">
            <Button type="button" onClick={() => setShowConfirmationModal(false)} size="md" variant="transparent">
              Cancel
            </Button>
            <Button
              variant="filled"
              size="md"
              onClick={() => {
                finalizeTransit(transit._id, form.values);
                setShowConfirmationModal(false);
              }}
            >
              Send
            </Button>
          </Flex>
        </Modal>
      )}
    </form>
  );
};
